import { domainSubjects, generalTest, languages } from "../data/subjects";

/**
 * Classify a subject into its type
 */
export function getSubjectType(subject) {
  if (languages.includes(subject)) return "Language";
  if (domainSubjects.includes(subject)) return "Domain";
  if (generalTest.includes(subject)) return "General Test";
  return null;
}

/**
 * Check if a combination is fully satisfied
 * Must account for subject reuse - each selected subject can only count once
 */
function isCombinationMet(combination, selectedSubjects) {
  const { requirements } = combination;

  // Track which subjects have been "used"
  let availableSubjects = [...selectedSubjects];

  // Sort requirements: specific requirements first (they're harder to satisfy)
  const sortedReqs = [...requirements].sort((a, b) => {
    if (a.specific && !b.specific) return -1;
    if (!a.specific && b.specific) return 1;
    return 0;
  });

  for (const req of sortedReqs) {
    const { count, type, specific } = req;
    let matched = 0;

    if (specific) {
      const idx = availableSubjects.findIndex(
        (s) => s === specific && getSubjectType(s) === type
      );
      if (idx !== -1) {
        availableSubjects.splice(idx, 1);
        matched = 1;
      }
      if (matched < count) return false;
    } else {
      const matchingIndices = [];
      for (let i = 0; i < availableSubjects.length && matched < count; i++) {
        if (getSubjectType(availableSubjects[i]) === type) {
          matchingIndices.push(i);
          matched++;
        }
      }
      if (matched < count) return false;
      // Remove used subjects in reverse order
      for (let i = matchingIndices.length - 1; i >= 0; i--) {
        availableSubjects.splice(matchingIndices[i], 1);
      }
    }
  }

  return true;
}

/**
 * For a combination, determine which requirements are met and which are missing
 */
export function getRequirementStatus(combination, selectedSubjects) {
  const { requirements } = combination;
  let availableSubjects = [...selectedSubjects];

  const sortedReqs = [...requirements].sort((a, b) => {
    if (a.specific && !b.specific) return -1;
    if (!a.specific && b.specific) return 1;
    return 0;
  });

  const statuses = [];

  for (const req of sortedReqs) {
    const { count, type, specific } = req;
    let matched = 0;

    if (specific) {
      const idx = availableSubjects.findIndex(
        (s) => s === specific && getSubjectType(s) === type
      );
      if (idx !== -1) {
        availableSubjects.splice(idx, 1);
        matched = 1;
      }
      statuses.push({ ...req, met: matched >= count });
    } else {
      const matchingIndices = [];
      for (let i = 0; i < availableSubjects.length && matched < count; i++) {
        if (getSubjectType(availableSubjects[i]) === type) {
          matchingIndices.push(i);
          matched++;
        }
      }
      const isMet = matched >= count;
      if (isMet) {
        for (let i = matchingIndices.length - 1; i >= 0; i--) {
          availableSubjects.splice(matchingIndices[i], 1);
        }
      }
      statuses.push({ ...req, met: isMet });
    }
  }

  // Re-sort back to original order
  const originalOrder = requirements.map((r) =>
    statuses.find(
      (s) =>
        s.type === r.type && s.specific === r.specific && s.count === r.count
    )
  );

  return originalOrder;
}

/**
 * Determine eligibility for a program
 * Returns: { eligible: boolean, bestCombination: combination | null, allCombinations: [...withStatus] }
 */
export function checkProgramEligibility(program, selectedSubjects) {
  let eligible = false;
  let bestCombination = null;

  const allCombinations = program.combinations.map((combo) => {
    const met = isCombinationMet(combo, selectedSubjects);
    const requirementStatuses = getRequirementStatus(combo, selectedSubjects);
    if (met && !bestCombination) {
      bestCombination = combo;
      eligible = true;
    }
    return { ...combo, met, requirementStatuses };
  });

  return { eligible, bestCombination, allCombinations };
}

/**
 * Process all programs and categorize them
 */
export function processEligibility(programs, selectedSubjects) {
  const eligiblePrograms = [];
  const otherPrograms = [];

  programs.forEach((program) => {
    const result = checkProgramEligibility(program, selectedSubjects);
    const programWithResult = { ...program, ...result };

    if (result.eligible) {
      eligiblePrograms.push(programWithResult);
    } else {
      otherPrograms.push(programWithResult);
    }
  });

  return { eligiblePrograms, otherPrograms };
}
